import { Component, EventEmitter, inject, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { Dtc } from '../../../../../shared/models/dtc/dtc';
import { RouterModule } from '@angular/router';
import { DtcTableSearchComponent } from '../../../search/dtc-table-search/dtc-table-search.component';
import { DeleteIconComponent } from '../../../icons/delete-icon/delete-icon.component';
import { DecisionModalComponent } from '../../../modals/decision-modal/decision-modal.component';
import { DtcQuery } from '../../../../../shared/models/dtc/dtc-query';
import { CommonModule } from '@angular/common';
import { EnabledByPolicyDirective } from '../../../../directives/enabled-by-policy.directive';
import { AgentActionDirective } from '../../../../directives/agent-action.directive';
import { Policy } from '../../../../../shared/models/info/policy';
import { Decision } from '../../../../../shared/models/internal/decision';
import { CommonUtils, getPropertyName } from '../../../../common/common-utils';
import { SortingConfiguration } from '../../../../../shared/internal/sorting-configuration';
import { HeaderSorterComponent } from '../../../common/tables/header-sorter/header-sorter.component';
import { NoteIconComponent } from '../../../icons/note-icon/note-icon.component';
import { ErrorMessage } from '../../../../../shared/models/error-message/error-message';
import { PreselectionService } from '../../../../services/preselection.service';
import { PreselectionType } from '../../../../../shared/enums/preselection-type';
import { RestoreIconComponent } from '../../../icons/restore-icon/restore-icon.component';
import { ScrollableBase } from '../../../base/scrollable-base';

@Component({
  selector: 'app-dtc-table',
  templateUrl: './dtc-table.component.html',
  styleUrl: './dtc-table.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    RouterModule,
    DtcTableSearchComponent,
    DeleteIconComponent,
    DecisionModalComponent,
    CommonModule,
    EnabledByPolicyDirective,
    AgentActionDirective,
    HeaderSorterComponent,
    NoteIconComponent,
    RestoreIconComponent,
  ],
})
export class DtcTableComponent extends ScrollableBase<DtcQuery> {
  @Input() public recyclingModeEnabled = false;
  @Input() public dtcs?: Dtc[];
  @Input() public softDeleteDisabled = false;
  @Input() public restoreDisabled = false;
  @Input() public hardDeleteDisabled = false;
  @Input() public selectedElementId?: string;
  @Output() public searchTriggered: EventEmitter<DtcQuery> = new EventEmitter<DtcQuery>();
  @Output() public softDeleteDtcTriggered: EventEmitter<Dtc> = new EventEmitter<Dtc>();
  @Output() public hardDeleteDtcTriggered: EventEmitter<Dtc> = new EventEmitter<Dtc>();
  @Output() public restoreDtcTriggered: EventEmitter<Dtc> = new EventEmitter<Dtc>();
  @Output() public downloadTriggered: EventEmitter<void> = new EventEmitter<void>();
  @Output() public dtcClicked: EventEmitter<string> = new EventEmitter<string>();
  @Output() public sortByFieldChanged: EventEmitter<DtcQuery> = new EventEmitter<DtcQuery>();

  public dtcToRemove?: Dtc;
  public policy = Policy;
  public commonUtils = CommonUtils;
  public getPropertyName = getPropertyName<Dtc>;
  public sortingConfiguration = new SortingConfiguration(this.getPropertyName('number'));

  private preselectionService = inject(PreselectionService);

  public onSearchTriggered(query: DtcQuery): void {
    this.query = query;
    const sortByField = this.sortingConfiguration.SortByField;
    if (sortByField) {
      this.query.orderBy = sortByField.field;
      this.query.orderByDescending = sortByField.orderByDescending;
    }
    this.searchTriggered.emit(query);
  }

  public onSoftDeleteDecision(decision: Decision): void {
    if (decision.result) {
      this.softDeleteDtcTriggered.emit(this.dtcToRemove);
    }
  }

  public onHardDeleteDecision(decision: Decision): void {
    if (decision.result) {
      this.hardDeleteDtcTriggered.emit(this.dtcToRemove);
    }
  }

  public showDecisionModal(id?: string, dtcValue?: string): void {
    this.dtcToRemove = { id: id, displayId: dtcValue };
  }

  public onDownloadTriggered(): void {
    this.downloadTriggered.emit();
  }

  public onDtcClicked(id?: string): void {
    this.dtcClicked.emit(id);
  }

  public restoreDtc(dtc: Dtc): void {
    this.restoreDtcTriggered.emit(dtc);
  }

  public sortBy(field: string): void {
    const sortByField = this.sortingConfiguration.sortBy(field);
    if (this.query) {
      this.query.orderBy = sortByField.field;
      this.query.orderByDescending = sortByField.orderByDescending;
      this.sortByFieldChanged.emit(this.query);
    }
  }

  public navigateToErrorMessage(em: ErrorMessage | undefined): void {
    if (!em || !em.id) {
      return;
    }

    this.preselectionService.configureAndChangeRoute(em.id, em.headerOnTerminal ?? '', PreselectionType.ErrorMessage);
  }

  public isSelected(id: string | undefined): boolean {
    return this.selectedElementId === id;
  }
}
